import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

const Deployment = () => {
  const [selectedCase, setSelectedCase] = useState(null);
  const [currentImage, setCurrentImage] = useState(0);
  const navigate = useNavigate();
  const [popupImage, setPopupImage] = useState(null);
  const [gallery, setGallery] = useState([]);
  const [galleryIndex, setGalleryIndex] = useState(0);

  const caseStudies = [
    {
      title: "BVRIT Campus – Energy Monitoring",
      images: [
        "/deployimage/bvritenergy.jpg",
        "/deployimage/onsite3.jpg",
        "/deployimage/bvritenergy3.png",
      ],
      problem: "No visibility on power usage across departments.",
      solution: "Installed IoT-based energy monitoring system.",
      result: "Improved monitoring and reduced energy wastage.",
    },
    {
      title: "MGIT – UPS Monitoring",
      images: [
        "/deployimage/mgit2.jpg",
        "/deployimage/mgit1.jpg",
        "/deployimage/mgit3.jpg",
        "/deployimage/mgit4.jpg",
        "/deployimage/mgit5.jpg",
      ],
      problem: "No real-time UPS status and failures.",
      solution: "Implemented UPS monitoring system.",
      result: "Improved reliability and faster fault detection.",
    },
    {
      title: "IIT Hyderabad – Smart IoT Farming",
      images: [
        "/deployimage/agri2.jpg",
        "/deployimage/agri1.jpg",
      ],
      problem: "Manual irrigation and inefficient water usage.",
      solution: "IoT-based automated irrigation system.",
      result: "Smart automation and water optimization achieved.",
    },
  ];

  return (
    <div className="relative min-h-screen text-white overflow-hidden">
      {/* 🔥 PREMIUM BACKGROUND */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#040816] via-[#0b0f19] to-[#111827] opacity-95" />

      {/* PREMIUM GLOW */}
      <div className="absolute inset-0 opacity-50 overflow-hidden">
        <div
          className="absolute top-[-15%] left-[-10%] w-[130%] h-[130%]
          bg-gradient-to-r from-orange-500/35 via-yellow-400/20 to-sky-500/30
          blur-[140px] animate-slowWave"
        />

        <div
          className="absolute bottom-[-15%] right-[-10%] w-[130%] h-[130%]
          bg-gradient-to-r from-sky-500/35 via-cyan-400/15 to-orange-400/25
          blur-[140px] animate-fastWave"
        />
      </div>

      {/* LIGHT SWEEP */}
      <div className="absolute inset-0 overflow-hidden opacity-20">
        <div className="absolute top-0 left-[-100%] w-[40%] h-full bg-gradient-to-r from-transparent via-white/20 to-transparent skew-x-[-20deg] animate-lightMove" />
      </div>

      {/* GRID */}
      <div
        className="pointer-events-none absolute inset-0 
        bg-[linear-gradient(to_right,rgba(255,255,255,0.04)_1px,transparent_1px),
             linear-gradient(to_bottom,rgba(255,255,255,0.04)_1px,transparent_1px)]
        bg-[size:60px_60px]"
      />

      {/* 🔶 CONTENT */}
      <div className="relative z-10 pb-20">
        {/* HERO */}
        <header className="py-20 text-center relative overflow-hidden">
          <div className="absolute inset-0 flex justify-center">
            <div className="w-[500px] h-[220px] bg-orange-500/20 blur-[140px]" />
          </div>

          <div className="relative z-10 animate-fadeUp">
            <h1
              className="text-5xl md:text-6xl font-extrabold
              bg-gradient-to-r from-orange-300 via-yellow-200 to-sky-300
              bg-clip-text text-transparent tracking-wide leading-tight"
            >
              Real Deployments Across Campuses
            </h1>

            <p className="mt-5 text-xl text-slate-300 max-w-3xl mx-auto leading-9">
              Power Sense solutions are deployed across top colleges with
              real-time monitoring and intelligent automation.
            </p>

            {/* STATS */}
            <div className="flex justify-center gap-10 mt-10 flex-wrap">
              {[
                { value: "4+", label: "Campuses" },
                { value: "IIT-H", label: "Smart Lab" },
                { value: "Multi", label: "Solutions" },
              ].map((item, i) => (
                <div
                  key={i}
                  className="group relative px-8 py-5 rounded-3xl
                  bg-white/[0.03] backdrop-blur-2xl border border-white/10
                  hover:border-orange-400/30
                  hover:shadow-[0_0_40px_rgba(255,170,0,0.25)]
                  hover:-translate-y-2
                  transition-all duration-500"
                >
                  <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-orange-500/10 to-sky-500/10 opacity-0 group-hover:opacity-100 transition duration-500" />

                  <h3 className="relative z-10 text-3xl font-extrabold text-yellow-300">
                    {item.value}
                  </h3>

                  <p className="relative z-10 text-slate-400 mt-2 tracking-wide">
                    {item.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </header>

        {/* CAMPUSES */}
        <section className="max-w-6xl mx-auto px-6 mt-10 animate-fadeUp">
          <h2
            className="text-4xl md:text-5xl font-extrabold text-center mb-12
            bg-gradient-to-r from-orange-300 via-yellow-200 to-sky-300
            bg-clip-text text-transparent tracking-wide"
          >
            Deployed Campuses
          </h2>

          <div className="grid md:grid-cols-4 gap-6 justify-items-center">
            {[
              { name: "BVRIT", img: "/deployimage/bvrit.png" },
              { name: "MGIT", img: "/deployimage/mgit.png" },
              { name: "CBIT", img: "/deployimage/cbit.png" },
              { name: "BITS Warangal", img: "/deployimage/bits.png" },
            ].map((item, i) => (
              <div
                key={i}
                className="group relative overflow-hidden
                bg-[#111827]/60 backdrop-blur-2xl rounded-3xl p-5 border border-white/10
                w-[220px] hover:scale-[1.04] hover:-translate-y-2
                hover:border-orange-400/30
                hover:shadow-[0_0_50px_rgba(255,170,0,0.25)]
                transition-all duration-500"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-orange-500/10 to-sky-500/10 opacity-0 group-hover:opacity-100 transition duration-500" />

                <img
                  src={item.img}
                  alt={item.name}
                  className="w-full h-40 object-cover rounded-2xl mb-4
                  transform group-hover:scale-105 transition duration-700"
                />

                <h3
                  className="text-center font-bold text-lg
                  bg-gradient-to-r from-yellow-300 to-orange-400
                  bg-clip-text text-transparent"
                >
                  {item.name}
                </h3>
              </div>
            ))}
          </div>
        </section>

        {/* ON SITE */}
        <section className="max-w-[1400px] mx-auto px-6 mt-20 bg-[#0b1220]/55 backdrop-blur-2xl rounded-3xl py-10 border border-white/10 animate-fadeUp">
          <h2
            className="text-4xl md:text-5xl font-extrabold text-center mb-12
            bg-gradient-to-r from-orange-300 via-yellow-200 to-sky-300
            bg-clip-text text-transparent"
          >
            On-Site Installations
          </h2>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              "onsite1.jpg",
              "onsite2.jpg",
              "onsite3.jpg",
              "onsite4.jpg",
              "onsite5.jpg",
              "onsite6.jpg",
            ].map((img, i) => (
              <div
                key={i}
                className="relative overflow-hidden rounded-3xl group cursor-pointer
                border border-white/10
                hover:border-orange-400/30
                hover:shadow-[0_0_50px_rgba(255,170,0,0.25)]
                hover:-translate-y-2
                transition-all duration-500"
                onClick={() => {
                  const images = [
                    "onsite1.jpg",
                    "onsite2.jpg",
                    "onsite3.jpg",
                    "onsite4.jpg",
                    "onsite5.jpg",
                    "onsite6.jpg",
                  ];
                  setGallery(images);
                  setGalleryIndex(i);
                }}
              >
                <img
                  src={`/deployimage/${img}`}
                  alt="On-site installation"
                  className="w-full h-52 object-cover
                  transform group-hover:scale-110 transition duration-700"
                />

                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition duration-500" />

                <div className="absolute bottom-4 left-4 text-white opacity-0 group-hover:opacity-100 transition duration-500">
                  View Installation
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* SOLUTIONS */}
        <section className="max-w-6xl mx-auto px-6 mt-20 bg-[#1a1030]/35 backdrop-blur-2xl rounded-3xl py-10 border border-white/10 animate-fadeUp">
          <h2
            className="text-4xl md:text-5xl font-extrabold text-center mb-12
            bg-gradient-to-r from-orange-300 via-yellow-200 to-sky-300
            bg-clip-text text-transparent"
          >
            Solutions Deployed
          </h2>

          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                title: "⚡ Energy Monitoring",
                desc: "BVRIT, MGIT, CBIT, BITS Warangal",
              },
              {
                title: "🔋 UPS Monitoring",
                desc: "MGIT College",
              },
              {
                title: "🌾 Smart IoT Farming",
                desc: "IIT Hyderabad",
              },
              {
                title: "☀️ Solar Monitoring",
                desc: "BVRIT, BITS Warangal",
              },
            ].map((item, i) => (
              <div
                key={i}
                className="group relative overflow-hidden
                bg-[#111827]/60 backdrop-blur-2xl rounded-3xl p-6 border border-white/10
                hover:border-orange-400/30
                hover:shadow-[0_0_50px_rgba(255,170,0,0.25)]
                hover:-translate-y-2
                transition-all duration-500"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-orange-500/10 to-sky-500/10 opacity-0 group-hover:opacity-100 transition duration-500" />

                <p className="relative z-10 font-semibold text-lg">
                  <span className="text-orange-400">{item.title}</span>

                  <span className="text-slate-400"> - </span>

                  <span className="text-yellow-300">{item.desc}</span>
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* CASE STUDIES */}
        <section className="max-w-[1400px] mx-auto px-6 mt-20 bg-[#111827]/55 backdrop-blur-2xl rounded-3xl py-10 border border-white/10 animate-fadeUp">
          <h2
            className="text-4xl md:text-5xl font-extrabold text-center mb-12
            bg-gradient-to-r from-orange-300 via-yellow-200 to-sky-300
            bg-clip-text text-transparent"
          >
            Real Deployment Case Studies
          </h2>

          <div className="grid md:grid-cols-3 gap-8">
            {caseStudies.map((item, i) => (
              <div
                key={i}
                onClick={() => {
                  setSelectedCase(item);
                  setCurrentImage(0);
                }}
                className="group cursor-pointer bg-[#111827]/60 backdrop-blur-2xl rounded-3xl overflow-hidden border border-white/10
                hover:border-orange-400/30
                hover:shadow-[0_0_60px_rgba(255,170,0,0.28)]
                hover:scale-[1.03]
                hover:-translate-y-2
                transition-all duration-500"
              >
                <img
                  src={item.images[0]}
                  alt={item.title}
                  className="w-full h-52 object-cover
                  transform group-hover:scale-110 transition duration-700"
                />

                <div className="p-6">
                  <h3 className="text-xl font-bold text-yellow-300">
                    {item.title}
                  </h3>

                  <p className="text-sm text-slate-400 mt-3">
                    Click to view details →
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* PROJECT COLLAB */}
        <section className="max-w-[1400px] mx-auto px-6 mt-20 bg-[#0f172a]/45 backdrop-blur-2xl rounded-3xl py-10 border border-white/10 animate-fadeUp">
          <h2
            className="text-4xl md:text-5xl font-extrabold text-center mb-10
            bg-gradient-to-r from-orange-300 via-yellow-200 to-sky-300
            bg-clip-text text-transparent"
          >
            Project Collaborations
          </h2>

          <div className="grid md:grid-cols-2 gap-6">
            {[
              "colab1.jpg",
              "colab2.jpg",
              "colab3.jpg",
              "colab4.jpg",
            ].map((img, i) => (
              <div
                key={i}
                className="relative overflow-hidden rounded-3xl group cursor-pointer
                border border-white/10
                hover:border-orange-400/30
                hover:shadow-[0_0_50px_rgba(255,170,0,0.25)]
                hover:-translate-y-2
                transition-all duration-500"
                onClick={() => {
                  const images = [
                    "colab1.jpg",
                    "colab2.jpg",
                    "colab3.jpg",
                    "colab4.jpg",
                  ];
                  setGallery(images);
                  setGalleryIndex(i);
                }}
              >
                <img
                  src={`/deployimage/${img}`}
                  alt=""
                  className="w-full h-52 object-cover
                  transform group-hover:scale-110 transition duration-700"
                />

                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition duration-500" />
              </div>
            ))}
          </div>
        </section>

        {/* WORKSHOPS */}
        <section className="max-w-[1400px] mx-auto px-6 mt-20 bg-[#451a03]/35 backdrop-blur-2xl rounded-3xl py-10 border border-white/10 animate-fadeUp">
          <h2
            className="text-4xl md:text-5xl font-extrabold text-center mb-10
            bg-gradient-to-r from-orange-300 via-yellow-200 to-sky-300
            bg-clip-text text-transparent"
          >
            Workshops & Training Sessions
          </h2>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              "workshop1.jpg",
              "workshop2.jpg",
              "workshop3.jpg",
              "workshop4.jpg",
              "workshop5.jpg",
              "workshop6.jpg",
              "workshop7.jpg",
              "workshop8.jpg",
              "workshop9.jpg",
            ].map((img, i) => (
              <div
                key={i}
                className="relative overflow-hidden rounded-3xl group cursor-pointer
                border border-white/10
                hover:border-orange-400/30
                hover:shadow-[0_0_50px_rgba(255,170,0,0.25)]
                hover:-translate-y-2
                transition-all duration-500"
                onClick={() => {
                  const images = [
                    "workshop1.jpg",
                    "workshop2.jpg",
                    "workshop3.jpg",
                    "workshop4.jpg",
                    "workshop5.jpg",
                    "workshop6.jpg",
                    "workshop7.jpg",
                    "workshop8.jpg",
                    "workshop9.jpg",
                  ];
                  setGallery(images);
                  setGalleryIndex(i);
                }}
              >
                <img
                  src={`/deployimage/${img}`}
                  alt=""
                  className="w-full h-52 object-cover
                  transform group-hover:scale-110 transition duration-700"
                />

                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition duration-500" />
              </div>
            ))}
          </div>
        </section>

        {/* IMPACT */}
        <section className="max-w-6xl mx-auto px-6 mt-20 text-center bg-[#1a1405]/35 backdrop-blur-2xl rounded-3xl py-10 border border-white/10 animate-fadeUp">
          <h2
            className="text-4xl md:text-5xl font-extrabold mb-12
            bg-gradient-to-r from-orange-300 via-yellow-200 to-sky-300
            bg-clip-text text-transparent"
          >
            Deployment Impact
          </h2>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { value: "30%+", text: "Energy Savings" },
              { value: "40%", text: "Downtime Reduced" },
              { value: "Real-Time", text: "Monitoring" },
              { value: "Instant", text: "Fault Alerts" },
            ].map((item, i) => (
              <div
                key={i}
                className="group bg-[#111827]/60 backdrop-blur-2xl p-7 rounded-3xl border border-white/10
                hover:border-orange-400/30
                hover:shadow-[0_0_50px_rgba(255,170,0,0.25)]
                hover:-translate-y-2
                transition-all duration-500"
              >
                <h3 className="text-3xl font-extrabold text-yellow-300">
                  {item.value}
                </h3>

                <p className="text-slate-400 mt-3">{item.text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* TRAINING */}
        <section className="max-w-6xl mx-auto px-6 mt-20 text-center animate-fadeUp">
          <h2
            className="text-4xl font-extrabold mb-6
            bg-gradient-to-r from-orange-300 via-yellow-200 to-sky-300
            bg-clip-text text-transparent"
          >
            Smart Grid Lab & Training
          </h2>

          <p className="text-slate-300 text-lg leading-8">
            Hands-on training at IIT Hyderabad with real-time smart grid systems.
          </p>
        </section>

        {/* CTA */}
        <section className="max-w-4xl mx-auto px-6 mt-20 text-center animate-fadeUp">
          <h2 className="text-4xl font-extrabold text-yellow-300">
            Bring Smart Energy Monitoring to Your Campus
          </h2>

          <button
            onClick={() => navigate("/enquiry")}
            className="mt-8 relative overflow-hidden
            bg-gradient-to-r from-orange-500 via-amber-400 to-yellow-300
            text-black font-bold tracking-wide
            px-8 py-4 rounded-2xl
            hover:scale-105 hover:shadow-[0_0_50px_rgba(255,170,0,0.45)]
            transition-all duration-500"
          >
            Request Deployment
          </button>
        </section>
      </div>

      {/* 🔥 MODAL */}
      {selectedCase && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-md"
          onClick={() => setSelectedCase(null)}
        >
          <div
            className="bg-[#0f172a]/95 border border-white/10 rounded-3xl max-w-lg w-full mx-4 overflow-hidden backdrop-blur-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative">
              <img
                src={selectedCase.images[currentImage]}
                alt=""
                className="w-full h-64 object-cover"
              />

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentImage((prev) =>
                    prev === 0 ? selectedCase.images.length - 1 : prev - 1
                  );
                }}
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 px-3 py-2 rounded-xl"
              >
                ◀
              </button>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentImage((prev) =>
                    prev === selectedCase.images.length - 1 ? 0 : prev + 1
                  );
                }}
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 px-3 py-2 rounded-xl"
              >
                ▶
              </button>
            </div>

            <div className="flex justify-center gap-2 mt-4">
              {selectedCase.images.map((_, i) => (
                <div
                  key={i}
                  onClick={(e) => {
                    e.stopPropagation();
                    setCurrentImage(i);
                  }}
                  className={`w-2.5 h-2.5 rounded-full cursor-pointer ${
                    currentImage === i ? "bg-yellow-400" : "bg-gray-500"
                  }`}
                />
              ))}
            </div>

            <div className="p-6">
              <h2 className="text-2xl font-bold text-yellow-300">
                {selectedCase.title}
              </h2>

              <p className="mt-4 text-slate-300">
                <b>Problem:</b> {selectedCase.problem}
              </p>

              <p className="mt-3 text-slate-300">
                <b>Solution:</b> {selectedCase.solution}
              </p>

              <p className="mt-3 text-slate-300">
                <b>Result:</b> {selectedCase.result}
              </p>

              <button
                onClick={() => setSelectedCase(null)}
                className="mt-6 w-full bg-gradient-to-r from-orange-500 to-yellow-400 text-black py-3 rounded-2xl font-bold hover:scale-[1.02] transition duration-300"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 🔥 GALLERY */}
      {gallery.length > 0 && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-md"
          onClick={() => setGallery([])}
        >
          <div onClick={(e) => e.stopPropagation()} className="relative">
            <img
              src={`/deployimage/${gallery[galleryIndex]}`}
              alt="Gallery"
              className="max-w-[85vw] max-h-[85vh] rounded-3xl border border-white/10"
            />

            <button
              onClick={() =>
                setGalleryIndex(
                  galleryIndex === 0 ? gallery.length - 1 : galleryIndex - 1
                )
              }
              className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 px-4 py-2 rounded-xl text-white"
            >
              ◀
            </button>

            <button
              onClick={() =>
                setGalleryIndex(
                  galleryIndex === gallery.length - 1 ? 0 : galleryIndex + 1
                )
              }
              className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 px-4 py-2 rounded-xl text-white"
            >
              ▶
            </button>
          </div>
        </div>
      )}

      {/* 🔥 ANIMATION */}
      <style>{`
        @keyframes slowWave {
          0% {
            transform: translate(-10%, -10%) rotate(0deg);
          }
          50% {
            transform: translate(10%, 10%) rotate(15deg);
          }
          100% {
            transform: translate(-10%, -10%) rotate(0deg);
          }
        }

        @keyframes fastWave {
          0% {
            transform: translate(10%, 10%) rotate(0deg);
          }
          50% {
            transform: translate(-10%, -10%) rotate(-15deg);
          }
          100% {
            transform: translate(10%, 10%) rotate(0deg);
          }
        }

        @keyframes lightMove {
          0% {
            transform: translateX(-120%) skewX(-20deg);
          }
          100% {
            transform: translateX(250%) skewX(-20deg);
          }
        }

        @keyframes fadeUp {
          0% {
            opacity: 0;
            transform: translateY(40px);
          }

          100% {
            opacity: 1;
            transform: translateY(0px);
          }
        }

        .animate-slowWave {
          animation: slowWave 16s ease-in-out infinite;
        }

        .animate-fastWave {
          animation: fastWave 12s ease-in-out infinite;
        }

        .animate-lightMove {
          animation: lightMove 8s linear infinite;
        }

        .animate-fadeUp {
          animation: fadeUp 1s ease forwards;
        }
      `}</style>
    </div>
  );
};

export default Deployment;